import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUp, Maximize2, Minimize2, Star, Users } from 'lucide-react'
import type { HouseholdMember, TodoCategory, Mode, UserId } from '@hewey/shared'
import { colorFor } from '@utils/colors.ts'
import { Dialog } from '@components/Dialog.tsx'
import { CategoryPicker } from './CategoryPicker.tsx'
import { AssigneePicker } from './AssigneePicker.tsx'
import { AssigneeChips } from './AssigneeChips.tsx'
import styles from './AddTodoDialog.module.css'

type Props = {
	open: boolean
	mode: Mode
	defaultCategory: TodoCategory | null
	existingCategories: TodoCategory[]
	members: HouseholdMember[]
	onClose: () => void
	onCreate: (input: {
		title: string
		category: TodoCategory | null
		priority: boolean
		assigneeIds: UserId[]
	}) => void
}

export const AddTodoDialog = ({
	open,
	mode,
	defaultCategory,
	existingCategories,
	members,
	onClose,
	onCreate,
}: Props) => {
	const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)
	const setInputRef = (el: HTMLTextAreaElement | HTMLInputElement | null) => {
		inputRef.current = el
	}

	const [title, setTitle] = useState('')
	const [category, setCategory] = useState<TodoCategory | null>(null)
	const [priority, setPriority] = useState(false)
	const [assigneeIds, setAssigneeIds] = useState<UserId[]>([])
	const [expanded, setExpanded] = useState(false)
	const [catPickerOpen, setCatPickerOpen] = useState(false)
	const [assigneePickerOpen, setAssigneePickerOpen] = useState(false)

	const memberMap = useMemo(
		() => new Map(members.map((m) => [m.id, m])),
		[members],
	)

	// Reset to a fresh draft each time the composer opens; default the lane to
	// whatever the board is currently showing.
	useEffect(() => {
		if (!open) return
		setTitle('')
		setCategory(defaultCategory)
		setPriority(false)
		setAssigneeIds([])
		setExpanded(false)
		setCatPickerOpen(false)
		setAssigneePickerOpen(false)
		queueMicrotask(() => inputRef.current?.focus())
	}, [open, defaultCategory])

	const toggleAssignee = (id: UserId) => {
		setAssigneeIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		)
	}

	const canSubmit = title.trim().length > 0

	const submit = () => {
		const trimmed = title.trim()
		if (trimmed.length === 0) return
		onCreate({
			title: trimmed.slice(0, 200),
			category,
			priority,
			assigneeIds: mode === 'family' ? assigneeIds : [],
		})
		onClose()
	}

	const catColor = category ? colorFor(category) : 'var(--ink-muted)'

	return (
		<Dialog open={open} onClose={onClose}>
			<div className={styles.panel}>
				{expanded ? (
					<textarea
						ref={setInputRef}
						className={styles.titleArea}
						value={title}
						maxLength={200}
						rows={4}
						placeholder={
							mode === 'family'
								? 'Add a family todo…'
								: 'Add a personal todo…'
						}
						onChange={(e) => setTitle(e.target.value)}
						aria-label="Task"
					/>
				) : (
					<input
						ref={setInputRef}
						className={styles.titleInput}
						value={title}
						maxLength={200}
						placeholder={
							mode === 'family'
								? 'Add a family todo…'
								: 'Add a personal todo…'
						}
						onChange={(e) => setTitle(e.target.value)}
						aria-label="Task"
					/>
				)}

				<div className={styles.controls}>
					<div className={styles.options}>
						<button
							type="button"
							className={styles.chip}
							onClick={() => setCatPickerOpen(true)}
						>
							<span
								className={styles.catDot}
								style={{ background: catColor }}
								aria-hidden="true"
							/>
							{category ?? 'general'}
						</button>

						{mode === 'family' && (
							<button
								type="button"
								className={styles.chip}
								onClick={() => setAssigneePickerOpen(true)}
								aria-label="Assign members"
							>
								{assigneeIds.length > 0 ? (
									<AssigneeChips
										members={memberMap}
										ids={assigneeIds}
									/>
								) : (
									<>
										<Users size={16} aria-hidden="true" />
										assign
									</>
								)}
							</button>
						)}

						<button
							type="button"
							className={styles.iconChip}
							onClick={() => setPriority((p) => !p)}
							aria-pressed={priority}
							aria-label={
								priority
									? 'Remove priority'
									: 'Mark as priority'
							}
							style={{
								color: priority
									? 'var(--accent-sunshine)'
									: 'var(--ink-muted)',
							}}
						>
							<Star
								size={18}
								aria-hidden="true"
								fill={priority ? 'currentColor' : 'none'}
							/>
						</button>

						<button
							type="button"
							className={styles.iconChip}
							onClick={() => setExpanded((x) => !x)}
							aria-pressed={expanded}
							aria-label={expanded ? 'Collapse' : 'Expand'}
						>
							{expanded ? (
								<Minimize2 size={18} aria-hidden="true" />
							) : (
								<Maximize2 size={18} aria-hidden="true" />
							)}
						</button>
					</div>

					<button
						type="button"
						className={styles.submit}
						onClick={submit}
						disabled={!canSubmit}
						aria-label="Add task"
					>
						<ArrowUp size={20} aria-hidden="true" />
					</button>
				</div>
			</div>

			<CategoryPicker
				open={catPickerOpen}
				current={category}
				existing={existingCategories}
				onPick={(c) => {
					setCategory(c)
					setCatPickerOpen(false)
				}}
				onClose={() => setCatPickerOpen(false)}
			/>

			<AssigneePicker
				open={assigneePickerOpen}
				members={members}
				selected={assigneeIds}
				onToggle={toggleAssignee}
				onClose={() => setAssigneePickerOpen(false)}
			/>
		</Dialog>
	)
}
